<template>
  <VLayout>
    <VFlex>
      <div :class="['password-component', showCriteria ? 'show-criteria' : '']">
        <VTextField
          ref="password"
          v-model="password"
          maxlength="72"
          class="flex-column password-field"
          :data-testid="testid"
          :append-icon="showPassword ? 'show' : 'hide'"
          :rules="passwordRules"
          :type="showPassword ? 'text' : 'password'"
          :username="username"
          :persistant-hint="warning"
          :hint="warning ? txc.usernameCantBePassword : ''"
          autocomplete="new-password"
          :validate-on-blur="validateOnBlur"
          required
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        >
          <template slot="label">
            <span class="field-label">{{ txc.newPassword }}</span>
          </template>
          <template slot="append">
            <span
              class="field-append-label"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? txc.hide : txc.show }}
            </span>
          </template>
        </VTextField>
        <div v-show="criteriaBlock && !warning" class="criteria-block">
          <ul>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includes8chars }"
            >
              <NsIcon
                v-if="!includes8chars"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includes8chars"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includes8chars }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesUpper }"
            >
              <NsIcon
                v-if="!includesUpper"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesUpper"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesUpper }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesLower }"
            >
              <NsIcon
                v-if="!includesLower"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesLower"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesLower }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesNumber }"
            >
              <NsIcon
                v-if="!includesNumber"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesNumber"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesNumber }}</div>
            </li>
            <li
              v-show="!criteriaComplete"
              :class="{ completed: includesSpecialChars }"
            >
              <NsIcon
                v-if="!includesSpecialChars"
                icon-name="icon-close-x"
                style="width: 18px; height: 18px"
              />
              <NsIcon
                v-if="includesSpecialChars"
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.includesSpecialChars }}</div>
            </li>
          </ul>
          <ul v-show="criteriaComplete" class="criteria-complete">
            <li class="completed">
              <NsIcon
                icon-name="icon-checkmark"
                style="width: 18px; height: 18px"
              />
              <div>{{ txc.criteriaComplete }}</div>
            </li>
          </ul>
        </div>
      </div>
    </VFlex>
  </VLayout>
</template>

<script>
/**
 * Original Author: Ben Layton
 * Original Team: Fore oh Four!
 */
import { VLayout, VFlex, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  name: 'NsOktaPassword',
  components: {
    VLayout,
    VTextField,
    VFlex,
    NsIcon,
  },
  props: {
    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default: () => {
        return {
          newPassword: 'New Password',
          passwordRequired: 'Password is required',
          excludesWhitespaceChars: 'Password must not include spaces',
          errorMustHave: 'Password must have:',
          errorUpper: '1 upper case',
          errorLower: '1 lower case',
          errorSpecial: '1 special character',
          errorNumber: '1 number',
          errorMinLength: 'at least 8 characters',
          errorMaxLength: 'no more than 72 characters',
          includes8chars: 'Use 8 or more characters',
          includesUpper: 'Use an upper case letter (e.g. A)',
          includesLower: 'Use a lower case letter (e.g. a)',
          includesNumber: 'Use a number (e.g. 1234)',
          passwordLength: 'Limit Password to 72 characters',
          includesSpecialChars: 'Use a symbol (e.g. !@#$)',
          dontIncludeNames:
            "Don't Include First, Last, or username in password",
          dontReusePassword: 'You cannot reuse a previous password',
          criteriaComplete: 'Your Password is STRONG',
          passwordConditionsMet: "Password doesn't meet minimum requirements",
          hide: 'HIDE',
          show: 'SHOW',
          usernameCantBePassword:
            'Your username cannot be part of your password',
        };
      },
    },
    /**
     * Validate using the password requirement criteria
     */
    criteriaOn: {
      type: Boolean,
      default: true,
    },
    hideCriteriaOnBlur: {
      type: Boolean,
      default: true,
    },
    rulesOn: {
      type: Boolean,
      default: true,
    },
    /**
     * Error code from Okta
     */
    externalStatusCode: {
      type: String,
      default: '',
    },
    /** Validate on Blur */
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    /**
     * String to add a testid for automation
     */
    testid: {
      type: String,
      default: '',
    },
    /**
     * username is passed to the password for validation
     */
    username: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      includes8chars: false,
      includesUpper: false,
      includesLower: false,
      includesNumber: false,
      includesSpecialChars: false,
      criteriaComplete: false,
      showCriteria: false,
      oktaCriteriaError: false,
      oktaPreviousError: false,
      includesUsername: false,
      criteria: {
        excludesWhitespaceChars: {
          regex: /(?=^\S+$)/,
        },
        includes8chars: {
          regex: /(?=.{8,})/,
        },
        includesUpper: {
          regex: /(?=.*[A-Z])/,
        },
        includesLower: {
          regex: /(?=.*[a-z])/,
        },
        includesNumber: {
          regex: /(?=.*[0-9])/,
        },
        includesSpecialChars: {
          regex: /(?=.*[!#$%&()*+,-./:;<=>?@[\]^_`{|"'}~])/,
        },
      },
      password: '',
      showPassword: false,
    };
  },
  computed: {
    criteriaBlock() {
      return this.criteriaOn && this.showCriteria;
    },
    passwordRules() {
      // Grab everything up front to help Vue know our deps
      const { txc, criteria, rulesOn } = this;
      if (!rulesOn) {
        return [true];
      }
      return [
        (v) => !!v || txc.passwordRequired || 'password required',
        (v) =>
          (v && criteria.excludesWhitespaceChars.regex.test(v)) ||
          txc.excludesWhitespaceChars ||
          'exclude whitespace',
        () =>
          this.passwordValid() ||
          txc.passwordConditionsMet ||
          'password not valid',
        (v) =>
          (v && v.length > 7) ||
          txc.includes8chars ||
          'password should be 8 chars',
        (v) =>
          (v && v.length < 73) ||
          txc.passwordLength ||
          'max length is 72 chars',
      ];
    },
    txc() {
      return this.translations;
    },
    warning() {
      return (
        this.password &&
        this.username &&
        this.password.toLowerCase().includes(this.username.toLowerCase())
      );
    },
  },
  watch: {
    password() {
      this.includes8chars =
        this.password &&
        this.password.match(this.criteria.includes8chars.regex);
      this.includesUpper =
        this.password && this.password.match(this.criteria.includesUpper.regex);
      this.includesLower =
        this.password && this.password.match(this.criteria.includesLower.regex);
      this.includesNumber =
        this.password &&
        this.password.match(this.criteria.includesNumber.regex);
      this.includesSpecialChars =
        this.password &&
        this.password.match(this.criteria.includesSpecialChars.regex);
      this.includesUsername =
        this.password &&
        this.username &&
        this.password.toLowerCase().includes(this.username.toLowerCase());
    },
    externalStatusCode() {
      if (this.externalStatusCode === 'E0000080') {
        this.oktaCriteriaError = true;
        this.criteriaComplete = false;
        this.showCriteria = true;
      } else if (this.externalStatusCode === 'E0000064') {
        this.oktaPreviousError = true;
        this.criteriaComplete = false;
        this.showCriteria = true;
      } else {
        this.oktaCriteriaError = false;
        this.oktaPreviousError = false;
      }
    },
  },
  methods: {
    onFocus() {
      this.showCriteria = true;
    },
    onBlur() {
      // TODO: why hide?
      this.showCriteria = !this.hideCriteriaOnBlur;
    },
    onInput() {
      /**
       * @event input
       * @type {string}
       */
      this.$emit('input', this.password);
      /**
       * @event valid
       * @type {boolean}
       */
      this.$emit('valid', this.passwordValid() && this.checkForUsername());
    },
    checkForUsername() {
      if (
        this.username &&
        this.password.toLowerCase().includes(this.username.toLowerCase())
      ) {
        this.warning = true;
        return false;
      } else {
        return true;
      }
    },
    passwordValid() {
      const errors = [];
      if (!this.password) {
        return false;
      }
      if (!this.password.match(this.criteria.includesUpper.regex)) {
        errors.push(this.txc.errorUpper);
      }
      if (!this.password.match(this.criteria.includesLower.regex)) {
        errors.push(this.txc.errorLower);
      }
      if (!this.password.match(this.criteria.includesSpecialChars.regex)) {
        errors.push(this.txc.errorSpecial);
      }
      if (!this.password.match(this.criteria.includesNumber.regex)) {
        errors.push(this.txc.errorNumber);
      }
      if (this.password.length < 8) {
        errors.push(this.txc.errorMinLength);
      }
      if (this.password.length > 72) {
        errors.push(this.txc.errorMaxLength);
      }
      if (this.oktaPreviousError) {
        errors.push(this.txc.dontReusePassword);
      }
      if (this.oktaCriteriaError) {
        errors.push(this.txc.passwordConditionsMet);
      }

      this.criteriaComplete = errors.length === 0;
      return (
        this.criteriaComplete || this.txc.errorMustHave + errors.join(', ')
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";

::v-deep * {
  $color-dryink: map.get(typographyVariables.$typography, font-family-primary);
}
$color-dryink: map.get(colors.$baseColors, dryink);
$color-error: map.get(colors.$baseColors, 'color-error');
$color-success: map.get(colors.$baseColors, success);
.v-label,
.v-icon {
  &.primary--text {
    color: $color-dryink !important;
  }
}

.password-component {
  vertical-align: center;

  ul {
    text-align: left;
    list-style: none;
    padding-left: 0;
    line-height: 26px;
    margin-bottom: 20px;

    li {
      display: flex;
      align-items: center;

      i {
        font-size: 15px;
        margin-right: 5px;
      }

      &:not(.completed) {
        i {
          color: #63666a;
        }
      }
    }

    &.criteria-complete {
      margin-top: -30px;
    }
  }

  .completed {
    color: $color-success;
  }
  .v-text-field__suffix {
    &.primary--text {
      color: $color-dryink !important;
    }
  }
  .field-append-label {
    &:hover {
      cursor: pointer;
    }
  }
  &.show-criteria {
    ::v-deep {
      .error--text .v-messages__message {
        display: none;
      }
      .field-append-label,
      .v-label {
        color: $color-dryink !important;
      }
    }
  }
}

.error_alert {
  border-style: solid;
  border-width: thin;
  border-color: red;
}
::v-deep {
  .password-field {
    input[type='password']:focus:not(.error--text) {
      border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
      color: map.get(colors.$theme, 'primary');
    }
  }
}
.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  font-size: 24px; /* Preferred icon size */
  display: inline-block;
  line-height: 1;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;

  /* Support for all WebKit browsers. */
  -webkit-font-smoothing: antialiased;
  /* Support for Safari and Chrome. */
  text-rendering: optimizeLegibility;

  /* Support for Firefox. */
  -moz-osx-font-smoothing: grayscale;

  /* Support for IE. */
  font-feature-settings: 'liga';
}
.v-text-field__suffix {
  right: 6px !important;
}
.v-text-field > .v-input__control > .v-input__slot:before {
  border: none;
}

.error--text {
  .field-append-label {
    color: $color-error;
  }
}
</style>
